import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Users, UserCheck, UserX, Shield, UserPlus } from 'lucide-react';

interface UserProfile {
  id: string;
  user_id: string;
  full_name: string;
  hourly_rate_chf: number;
  created_at: string;
  user_roles?: {
    role: string;
  }[];
}

const AdminUserManagement = () => {
  const { user, loading, userRole } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [newEmployee, setNewEmployee] = useState({
    email: '',
    password: '',
    full_name: '',
    hourly_rate_chf: '25.00'
  });

  useEffect(() => {
    if (!loading && user) {
      console.log('User role in AdminUserManagement:', userRole);
      if (userRole === 'ADMIN' || userRole === 'admin') {
        fetchUsers();
      } else {
        setIsLoading(false);
      }
    }
  }, [user, userRole, loading]);

  // Redirect if not authenticated or not admin
  if (!loading && !user) {
    return <Navigate to="/auth" replace />;
  }

  if (!loading && user && userRole !== 'ADMIN' && userRole !== 'admin') {
    return <Navigate to="/hours" replace />;
  }

  if (loading || !userRole) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-je-red mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  const fetchUsers = async () => {
    try {
      // Récupérer les profils
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (profilesError) throw profilesError;

      // Récupérer les rôles séparément
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('*');

      if (rolesError) throw rolesError;

      // Combiner les données
      const usersWithRoles = (profilesData || []).map(profile => ({
        ...profile,
        user_roles: rolesData?.filter(role => role.user_id === profile.user_id).map(role => ({ role: role.role })) || []
      }));

      setUsers(usersWithRoles);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de charger les utilisateurs"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const assignRole = async (userId: string, role: 'ADMIN' | 'EMPLOYEE') => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .insert([{ user_id: userId, role }]);

      if (error) throw error;

      toast({
        title: "Succès",
        description: `Rôle ${role} attribué avec succès`
      });

      fetchUsers();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message
      });
    }
  };

  const removeRole = async (userId: string, role: 'ADMIN' | 'EMPLOYEE') => {
    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', userId)
        .eq('role', role);

      if (error) throw error;

      toast({
        title: "Succès",
        description: `Rôle ${role} retiré avec succès`
      });

      fetchUsers();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message
      });
    }
  };

  const createEmployee = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreating(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const response = await fetch(
        'https://nnleruzughmviocbvnwa.supabase.co/functions/v1/create-employee',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: newEmployee.email,
            password: newEmployee.password,
            full_name: newEmployee.full_name,
            hourly_rate_chf: parseFloat(newEmployee.hourly_rate_chf),
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create employee');
      }

      toast({
        title: "Succès",
        description: `Employé ${newEmployee.full_name} créé avec succès`
      });

      setNewEmployee({
        email: '',
        password: '',
        full_name: '',
        hourly_rate_chf: '25.00'
      });

      fetchUsers();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message
      });
    } finally {
      setIsCreating(false);
    }
  };

  const resetUserPassword = async (userId: string, userName: string) => {
    const newPassword = prompt(`Entrez le nouveau mot de passe pour ${userName}:`);
    
    if (!newPassword) {
      return;
    }

    if (newPassword.length < 6) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Le mot de passe doit contenir au moins 6 caractères"
      });
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const response = await fetch(
        'https://nnleruzughmviocbvnwa.supabase.co/functions/v1/reset-user-password',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId,
            newPassword
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to reset password');
      }

      toast({
        title: "Succès",
        description: `Le mot de passe de ${userName} a été réinitialisé`
      });
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible de réinitialiser le mot de passe"
      });
    }
  };

  const deleteUser = async (userId: string, userName: string) => {
    const confirmDelete = confirm(
      `⚠️ ATTENTION ⚠️\n\nÊtes-vous sûr de vouloir supprimer l'utilisateur "${userName}" ?\n\nCette action est IRRÉVERSIBLE et supprimera :\n- Le compte utilisateur\n- Son profil\n- Ses rôles\n- Toutes ses heures de travail\n\nTapez "SUPPRIMER" pour confirmer.`
    );
    
    if (!confirmDelete) {
      return;
    }

    const finalConfirm = prompt(`Pour confirmer la suppression de ${userName}, tapez "SUPPRIMER" :`);
    
    if (finalConfirm !== 'SUPPRIMER') {
      toast({
        title: "Suppression annulée",
        description: "La confirmation n'a pas été validée"
      });
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const response = await fetch(
        'https://nnleruzughmviocbvnwa.supabase.co/functions/v1/delete-user',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete user');
      }

      toast({
        title: "Utilisateur supprimé",
        description: `${userName} a été supprimé du système`
      });

      fetchUsers();
    } catch (error: any) {
      console.error('Error deleting user:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: error.message || "Impossible de supprimer l'utilisateur"
      });
    }
  };

  const getUserRole = (user: UserProfile) => {
    return user.user_roles?.[0]?.role || 'Aucun rôle';
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'ADMIN': return 'default';
      case 'EMPLOYEE': return 'secondary';
      default: return 'outline';
    }
  };

  const pendingUsers = users.filter(u => !u.user_roles || u.user_roles.length === 0);
  const approvedUsers = users.filter(u => u.user_roles && u.user_roles.length > 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">JE-HEPIA</h1>
              <div className="ml-6">
                <span className="text-sm text-gray-500">Gestion des Utilisateurs</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="text-gray-600 hover:text-je-red"
              >
                Payroll Management
              </Button>
              <span className="text-sm text-gray-700">
                Admin: {user?.email}
              </span>
              <Button 
                variant="outline" 
                onClick={() => supabase.auth.signOut()}
              >
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="flex items-center p-6">
              <Users className="h-8 w-8 text-je-red mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-600">Total Utilisateurs</p>
                <p className="text-2xl font-bold text-gray-900">{users.length}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center p-6">
              <UserCheck className="h-8 w-8 text-green-600 mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-600">Validés</p>
                <p className="text-2xl font-bold text-gray-900">{approvedUsers.length}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center p-6">
              <UserX className="h-8 w-8 text-orange-600 mr-4" />
              <div>
                <p className="text-sm font-medium text-gray-600">En Attente</p>
                <p className="text-2xl font-bold text-gray-900">{pendingUsers.length}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Create New Employee */}
        <Card className="mb-8 border-green-500 border-2">
          <CardHeader>
            <CardTitle className="text-green-600 flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Créer un Nouvel Employé
            </CardTitle>
            <CardDescription>
              Ajouter un employé au système avec ses identifiants
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={createEmployee} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="full_name">Nom Complet</Label>
                  <Input
                    id="full_name"
                    value={newEmployee.full_name}
                    onChange={(e) => setNewEmployee({ ...newEmployee, full_name: e.target.value })}
                    required
                    placeholder="Samuel Cottier-Angeli"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newEmployee.email}
                    onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })}
                    required
                    placeholder="samuel@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Mot de Passe</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newEmployee.password}
                    onChange={(e) => setNewEmployee({ ...newEmployee, password: e.target.value })}
                    required
                    placeholder="Mot de passe sécurisé"
                  />
                </div>
                <div>
                  <Label htmlFor="hourly_rate">Taux Horaire (CHF)</Label>
                  <Input
                    id="hourly_rate"
                    type="number"
                    step="0.01"
                    value={newEmployee.hourly_rate_chf}
                    onChange={(e) => setNewEmployee({ ...newEmployee, hourly_rate_chf: e.target.value })}
                    required
                    placeholder="30.00"
                  />
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={isCreating}
              >
                {isCreating ? 'Création en cours...' : 'Créer Employé'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Code Admin Ultra Protégé */}
        <Card className="mb-8 border-je-red border-2">
          <CardHeader>
            <CardTitle className="text-je-red flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Code Admin Ultra Protégé
            </CardTitle>
            <CardDescription>
              Code à utiliser pour créer des comptes administrateur
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-100 p-4 rounded-lg font-mono text-lg font-bold text-center border-2 border-dashed border-je-red">
              HEPIA-2025-ULTRA-SECURE-9X4M7P8Q2Z
            </div>
            <p className="text-sm text-gray-600 mt-2">
              ⚠️ Gardez ce code secret ! Il permet de créer des comptes administrateur.
            </p>
          </CardContent>
        </Card>

        {/* Pending Users */}
        {pendingUsers.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-orange-600">Utilisateurs en Attente de Validation</CardTitle>
              <CardDescription>
                Ces utilisateurs ont créé un compte et attendent votre validation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Date d'inscription</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.full_name}</TableCell>
                      <TableCell>{user.user_id}</TableCell>
                      <TableCell>{new Date(user.created_at).toLocaleDateString('fr-FR')}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => assignRole(user.user_id, 'EMPLOYEE')}
                          >
                            ✓ Valider Employé
                          </Button>
                          <Button 
                            size="sm" 
                            className="bg-je-red hover:bg-red-700"
                            onClick={() => assignRole(user.user_id, 'ADMIN')}
                          >
                            ✓ Valider Admin
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* All Users */}
        <Card>
          <CardHeader>
            <CardTitle>Tous les Utilisateurs</CardTitle>
            <CardDescription>
              Gestion des rôles et permissions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8">Chargement...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Rôle</TableHead>
                    <TableHead>Taux Horaire (CHF)</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => {
                    const role = getUserRole(user);
                    return (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.full_name}</TableCell>
                        <TableCell>{user.user_id}</TableCell>
                        <TableCell>
                          <Badge variant={getRoleBadgeVariant(role)}>
                            {role}
                          </Badge>
                        </TableCell>
                        <TableCell>{user.hourly_rate_chf} CHF</TableCell>
                        <TableCell>
                          <div className="flex gap-2 flex-wrap">
                            {role === 'Aucun rôle' && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => assignRole(user.user_id, 'EMPLOYEE')}
                                >
                                  Employé
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => assignRole(user.user_id, 'ADMIN')}
                                >
                                  Admin
                                </Button>
                              </>
                            )}
                            {role !== 'Aucun rôle' && (
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => removeRole(user.user_id, role as 'ADMIN' | 'EMPLOYEE')}
                              >
                                Retirer rôle
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="secondary"
                              onClick={() => resetUserPassword(user.user_id, user.full_name)}
                            >
                              🔑 Réinitialiser MDP
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => deleteUser(user.user_id, user.full_name)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              🗑️ Supprimer
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminUserManagement;